/**
 * Hash-based allowlist of preset themes that are allowed to bypass the pro
 * plan check for theming.
 *
 * Instead of hashing the full muiTheme (which is fragile to default-filling
 * and format transformations in the ThemeEditor pipeline), we extract a
 * fingerprint from 6 identity-defining properties:
 *
 *   primary.main | secondary.main | header.main | background.page | fontFamily | borderRadius
 *
 * These uniquely identify each preset and are never transformed by the
 * extractFormValues → cleanThemeForOutput round-trip.
 *
 * Keep this list in sync with the presets defined in the React repo at
 * `timum_react_components/src/components/ThemeEditor/themePresets.js`.
 * Whenever a preset's palette.primary.main, palette.secondary.main,
 * palette.header.main, palette.background.page, typography.fontFamily or
 * shape.borderRadius changes, recompute its hash via
 * `fnv1a32(getThemeFingerprint(preset.theme))` below and update the entry.
 */

// ---------------------------------------------------------------------------
// FNV-1a 32-bit hashes of preset fingerprints
// ---------------------------------------------------------------------------

const PRESET_FINGERPRINT_HASHES = new Set([
  '565d95c7', // zen      — Wellness & Spa        (Cormorant Garamond, 12px radius)
  'bcf168b5', // pulse    — Energy / gym / club    (Oswald, 0 radius, dark #121212)
  '68137a69', // estate   — Real Estate            (DM Sans, 2 radius)
  '41a1790c', // counsel  — Legal & Professional   (Libre Baskerville, 2 radius)
  '02a9e46a', // clinic   — Medical & Healthcare   (Source Sans 3, 8 radius)
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract the identity fingerprint from a muiTheme object.
 * Concatenates the 6 properties that define a preset's visual identity.
 */
function getThemeFingerprint(muiTheme) {
  const p = muiTheme.palette || {};
  return [
    (p.primary || {}).main || '',
    (p.secondary || {}).main || '',
    (p.header || {}).main || '',
    (p.background || {}).page || (p.background || {}).default || '',
    (muiTheme.typography || {}).fontFamily || '',
    String((muiTheme.shape || {}).borderRadius || 0),
  ].join('|');
}

/**
 * FNV-1a 32-bit hash – fast, synchronous, collision-resistant enough for a
 * small allowlist.
 *
 * @param {string} str
 * @returns {string} Hex-encoded 32-bit hash
 */
function fnv1a32(str) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Check whether a muiTheme object matches one of the preset themes.
 *
 * Extracts a fingerprint from 6 identity properties and checks the FNV-1a
 * hash against the allowlist. This is robust against default-filling and
 * format transformations that occur in the ThemeEditor pipeline.
 *
 * @param {object} muiTheme - The theme object to check
 * @returns {boolean} True if the theme matches a preset
 */
export function isPresetThemeSync(muiTheme) {
  if (!muiTheme || typeof muiTheme !== 'object') return false;
  return PRESET_FINGERPRINT_HASHES.has(fnv1a32(getThemeFingerprint(muiTheme)));
}
